import { useMemo } from 'react';
/**
 * DISCLAIMER: This is written as a React hook but it doesn't currently make user of any React hooks or lifecycle
 * methods. The reason we write this as a hook is that we will ultimately move this logic out of angular services and
 * either into this file or some other service, but we will need access to the user data to be able to check features.
 * This data will likely be stored in some store (context/redux) and we will need other hooks in here. So the structure
 * of this file is to future proof for when we have enough pieces in place to write this as a real hook.
 *
 * If you are writing a similar angular bridging hook that doesn't have any internal state or use other react hooks,
 * please copy this disclaimer to your new hook.
 */
import type { DefaultChurnSurvey } from '@hotjar/webapp/domains/revenue/churn-survey/types';
import type {
  GetSurveyResponse,
  GetSurveyResponseQuestion,
  SubmitChurnSurveyResponse,
} from '@hotjar/webapp/domains/revenue/churn-survey/dto';
import { getAngularService } from '../../utils/angular-injector';
import { useNgBridgeChecker } from '../useNgBridgeChecker';

interface UseChurnSurvey {
  get: () => Promise<GetSurveyResponse>;
  submit: (
    userResponse: DefaultChurnSurvey,
    questionsFromServer: GetSurveyResponseQuestion[],
  ) => SubmitChurnSurveyResponse;
}

export function useChurnSurvey(): UseChurnSurvey {
  useNgBridgeChecker();

  return useMemo(() => {
    const { get, submit } = getAngularService('churnSurvey');

    return {
      get,
      submit: (userResponse, questionsFromServer) => submit(userResponse, questionsFromServer),
    };
  }, []);
}
